import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTableStatic } from "@/components/datatable";
import type { ColumnDef } from "@/components/datatable";
import { PLANNER_PERMISSIONS } from "@/constants/permission";
import { GetEditableColumns } from "@/hooks/services/pps/handlers/editable-columns-api";
import { EditableColumns } from "@/hooks/services/pps/types/editable-columns.type";
import { useGetData } from "@/hooks/services/use-query-hooks";
import { useUserProfileStore } from "@/stores/user-profile-store";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Settings, Pencil } from "lucide-react";

export const Route = createFileRoute("/settings/editable-columns/")({
  component: RouteComponent,
  beforeLoad: () => {
    const { userProfile } = useUserProfileStore.getState();
    if (userProfile?.rolePermissions) {
      const hasPermission = userProfile.rolePermissions.includes(
        PLANNER_PERMISSIONS.PLANT3_EDITABLE_COLUMN,
      );
      if (!hasPermission) {
        throw redirect({ to: "/plant-3/list" });
      }
    }
  },
});

function RouteComponent() {
  const editableColumns = useGetData(GetEditableColumns());
  const navigate = useNavigate();

  const columns: ColumnDef<EditableColumns>[] = [
    {
      accessorKey: "columnLabel",
      header: "Column",
      cell: ({ row }) => row.original.columnLabel,
    },
    {
      accessorKey: "properties.isEditable",
      header: "Is Editable",
      cell: ({ row }) =>
        row.original.properties.isEditable ? (
          <Badge variant="success">Yes</Badge>
        ) : (
          <Badge variant="secondary">No</Badge>
        ),
    },
  ];

  return (
    <div className="aero:flex aero:flex-col aero:gap-4 aero:w-full aero:max-w-[1104px] aero:mx-auto">
      {/* Page Title */}
      <div className="aero:flex aero:items-center aero:gap-3">
        <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
          <Settings className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
        </div>
        <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
          Editable Columns Settings
        </h1>
      </div>

      {/* Main Card */}
      <Card className="aero:!rounded-lg aero:!border-neutral-300 aero:!p-4 aero:!gap-2 aero:!shadow-none">
        {/* Header with Edit Button */}
        <div className="aero:flex aero:items-center aero:justify-between aero:pb-4 aero:border-b aero:border-neutral-400">
          <div className="aero:flex aero:items-center aero:gap-2">
            <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
            <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
              Column Configuration
            </h2>
          </div>
          <Button
            type="button"
            onClick={() =>
              navigate({
                to: `/settings/editable-columns/edit`,
              })
            }
            className="aero:flex aero:items-center aero:gap-2"
          >
            <Pencil className="aero:h-4 aero:w-4" />
            Edit
          </Button>
        </div>

        {/* Table Content */}
        <CardContent className="aero:!p-0 aero:!pt-4">
          <DataTableStatic
            data={editableColumns.data?.data || []}
            columns={columns}
            features={{
              enableSelection: false,
              enablePagination: false,
              enableSorting: true,
              enableColumnDragAndDrop: false,
              enablePinning: false,
              enableResizing: false,
            }}
            defaultPageSize={100}
            isLoading={editableColumns.isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}
